import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "../../redux/slices/modalSlice";
import {
  FiHome,
  FiUsers,
  FiCode,
  FiSettings,
  FiGrid,
  FiHash,
  FiBarChart2,
  FiCreditCard,
  FiInbox,
  FiMessageSquare,
  FiBell,
  FiMenu,
  FiX,
  FiLogOut,
  FiCpu,
  FiZap,
} from "react-icons/fi";

export const ADMIN_NAV = [
  { key: "overview", label: "Overview", icon: FiHome },
  { key: "analytics", label: "Analytics", icon: FiBarChart2 },
  { key: "channels", label: "Channels", icon: FiHash },
  { key: "setup", label: "Channels AI", icon: FiZap },
  { key: "members", label: "Members", icon: FiUsers },
  { key: "inbox", label: "Talk to us", icon: FiMessageSquare },
  { key: "requests", label: "Requests", icon: FiInbox },
  { key: "notifications", label: "Notifications", icon: FiBell },
  { key: "integration", label: "Integration", icon: FiCode },
  { key: "bot", label: "Bot Studio", icon: FiCpu },
  { key: "billing", label: "Billing", icon: FiCreditCard },
  { key: "settings", label: "Settings", icon: FiSettings },
];

// Maps the business's onboarding/verification state to a status pill.
const getStatus = (business) => {
  if (!business) return { label: "Setup in progress", tone: "warning" };
  const s = business.onboardingState;
  // Keyed on the onboarding flow, not business.isVerified (which is true for
  // any paid business and isn't a domain-verification signal).
  if (s === "embed_live" || s === "complete")
    return { label: "Live", tone: "success" };
  if (["domain_verified", "draft_ready", "community_committed"].includes(s))
    return { label: "Domain verified", tone: "success" };
  if (s === "domain_pending")
    return { label: "Awaiting DNS", tone: "warning" };
  return { label: "Setup in progress", tone: "warning" };
};

const StatusPill = ({ business }) => {
  const { label, tone } = getStatus(business);
  const toneClass =
    tone === "success"
      ? "text-theme-success border-theme-success"
      : "text-theme-warning border-theme-warning";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${toneClass}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
};

// Top-right account button: shows initials, opens a dropdown with the admin's
// name/email and a Sign out action (routes through the shared LogoutModal).
const ProfileMenu = ({ name, email }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-theme-accent text-xs font-bold text-theme-accentText outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2 focus-visible:ring-offset-theme-secondaryBackground"
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-theme-chatDivider bg-theme-secondaryBackground shadow-xl"
        >
          <div className="border-b border-theme-chatDivider px-4 py-3">
            <p className="truncate text-sm font-semibold text-theme-secondaryText">
              {name}
            </p>
            {email && (
              <p className="truncate text-xs text-theme-description">{email}</p>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              dispatch(openModal({ modalName: "modalLogoutOpen" }));
            }}
            className="flex w-full items-center gap-2.5 px-4 py-3 text-sm font-medium text-theme-error transition-colors hover:bg-theme-tertiaryBackground"
          >
            <FiLogOut size={16} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const AdminShell = ({ activeNav, onNavChange, business, allowedNav, inboxUnread = 0, notifUnread = 0, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const businessName = business?.name || "Your business";
  const adminName = business?.user_id?.name || business?.name || "Admin";
  const adminEmail = business?.user_id?.email || business?.email || "";
  // Only expose the nav items unlocked for the current onboarding tier
  // (see AdminHome: billing -> +overview -> full dashboard).
  const allowed = allowedNav || ["billing"];
  const navItems = ADMIN_NAV.filter((n) => allowed.includes(n.key));

  const NavList = () => (
    <nav className="flex flex-col gap-1 px-3">
      {navItems.map(({ key, label, icon: Icon }) => {
        const active = key === activeNav;
        return (
          <button
            key={key}
            onClick={() => {
              // Every item (incl. "Channels AI") is an in-shell tab now, so the
              // admin sidebar always stays. The Channels AI tab renders the AI
              // drafting for Growth / first-time, or a locked preview otherwise.
              onNavChange(key);
              setMobileOpen(false);
            }}
            className={`group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              active
                ? "bg-theme-accentSoft text-theme-accent"
                : "text-theme-primaryText hover:bg-theme-tertiaryBackground hover:text-theme-secondaryText"
            }`}
          >
            {active && (
              <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-theme-accent" />
            )}
            <Icon size={18} />
            {label}
            {key === "inbox" && inboxUnread > 0 && (
              <span className="ml-auto h-2 w-2 flex-shrink-0 rounded-full bg-theme-accent" />
            )}
            {key === "notifications" && notifUnread > 0 && (
              <span className="ml-auto h-2 w-2 flex-shrink-0 rounded-full bg-theme-accent" />
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="flex h-screen w-full flex-col bg-theme-primaryBackground">
      {/* Top header */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-theme-chatDivider px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            className="text-theme-primaryText md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-theme-accent">
            <FiGrid size={16} className="text-theme-accentText" />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-bold text-theme-secondaryText">Channels</span>
            <span className="text-theme-placeholder">/</span>
            <span className="font-medium text-theme-primaryText">
              {businessName}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill business={business} />
          <ProfileMenu name={adminName} email={adminEmail} />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Sidebar (desktop) */}
        <aside className="hidden w-60 flex-shrink-0 border-r border-theme-chatDivider bg-theme-sidebarBackground py-4 md:block">
          <NavList />
        </aside>

        {/* Sidebar (mobile drawer) */}
        {mobileOpen && (
          <>
            <div
              className="fixed inset-0 z-30 bg-black/50 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <aside className="fixed left-0 top-0 z-40 h-full w-60 border-r border-theme-chatDivider bg-theme-sidebarBackground py-4 md:hidden">
              <div className="mb-4 px-4">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="text-theme-primaryText"
                  aria-label="Close menu"
                >
                  <FiX size={20} />
                </button>
              </div>
              <NavList />
            </aside>
          </>
        )}

        {/* Content */}
        <main className="min-w-0 flex-1 overflow-y-auto custom-scrollbar">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
